import { suggestionsForConditions, type Pet, type SuggestedRecord } from '@petrhythm/core';

/** 기록 허브 격자·오늘 빠른 기록·바로가기 고르기가 같이 쓰는 기록 종류 목록. */
export interface RecordKind {
  key: SuggestedRecord;
  label: string;
  detail: string;
  path: (petId: string) => string;
  species?: Pet['species'];
}

export const RECORD_KINDS: readonly RecordKind[] = [
  { key: 'srr', label: '수면 호흡수', detail: '자는 동안 탭으로 세기', path: (id) => `/app/record/srr/${id}` },
  { key: 'resp', label: '호흡기 사건', detail: '지속 시간과 종류', path: (id) => `/app/record/resp/${id}` },
  { key: 'cough', label: '기침', detail: '낮·밤 횟수', path: (id) => `/app/record/cough/${id}` },
  { key: 'weight', label: '체중', detail: '저울·안고 재기', path: (id) => `/app/record/weight/${id}` },
  { key: 'meal', label: '식사', detail: '준 양·남긴 양', path: (id) => `/app/record/meal/${id}` },
  { key: 'treatment', label: '약·처치', detail: '계획과 실제 결과', path: (id) => `/app/treatments/${id}` },
  { key: 'event', label: '사건·사진', detail: '있었던 일과 첨부', path: (id) => `/app/record/event/${id}` },
  { key: 'gait', label: '산책·보행', detail: '시간 재기·절뚝임', path: (id) => `/app/record/gait/${id}` },
  { key: 'checkin', label: '컨디션', detail: '식욕·활력·수면', path: (id) => `/app/record/checkin/${id}` },
  { key: 'qol', label: '삶의 질', detail: '보호자가 본 7가지', path: (id) => `/app/record/qol/${id}` },
  { key: 'stool', label: '배변', detail: '모양·색·사진', path: (id) => `/app/record/elimination/${id}` },
  { key: 'urine', label: '소변·감자', detail: '횟수·감자 크기', path: (id) => `/app/record/elimination/${id}?kind=urine` },
  { key: 'water', label: '물그릇', detail: '채운 양·남은 양', path: (id) => `/app/record/water/${id}` },
  { key: 'seizure', label: '발작 시간', detail: '시작·멈춤·회복', path: (id) => `/app/record/seizure/${id}` },
  { key: 'glucose', label: '혈당 곡선', detail: '하루 여러 번 잰 값', path: (id) => `/app/record/glucose/${id}` },
  { key: 'vitals', label: '체온·심박', detail: '숫자로 남기기', path: (id) => `/app/record/vitals/${id}` },
];

const FALLBACK: SuggestedRecord[] = ['srr', 'weight', 'meal', 'checkin', 'cough', 'event'];

/** 질환 묶음 순서로 정렬한다. 질환을 골라야만 열리는 종류는 없다. */
export function orderedRecordKinds(pet: Pick<Pet, 'conditions' | 'species'>): RecordKind[] {
  const available = RECORD_KINDS.filter((kind) => !kind.species || kind.species === pet.species);
  const suggested = suggestionsForConditions(pet.conditions);
  const rank = (key: SuggestedRecord) => { const index = suggested.indexOf(key); return index < 0 ? 100 + available.findIndex((kind) => kind.key === key) : index; };
  return [...available].sort((a, b) => rank(a.key) - rank(b.key));
}

export function quickRecordDefaults(pet: Pick<Pet, 'conditions' | 'species'>): SuggestedRecord[] {
  const keys = new Set<SuggestedRecord>();
  for (const key of [...suggestionsForConditions(pet.conditions), ...FALLBACK]) {
    if (RECORD_KINDS.some((kind) => kind.key === key && (!kind.species || kind.species === pet.species))) keys.add(key);
    if (keys.size === 4) break;
  }
  return [...keys];
}

export function recordKind(key: string): RecordKind | undefined {
  return RECORD_KINDS.find((kind) => kind.key === key);
}
